import React, { useEffect, useState } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Row, 
  Col, 
  Upload, 
  App, 
  Divider, 
  Typography, 
  Spin,
  Space,
  Alert,
  Switch,
  Select,
} from 'antd';
import { useNavigate, Link } from 'react-router-dom';
import { SaveOutlined, UploadOutlined, BuildOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useCompanyProfileSettings, useUpdateCompanyProfileSettings } from '@/hooks/useSettings';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useCurrency } from '@/contexts/CurrencyContext';
import { currencyService } from '@/services/currency.service';
import api from '@/services/api.service';
import { UAE_EMIRATES, UAE_PAYMENT_MEANS, LEGAL_REGISTRATION_TYPES } from '@/constants/uaeEinvoice';
import { LetterheadImageField } from '@/components/print/LetterheadImageField';
import { DEFAULT_LETTERHEAD } from '@/print-engine/page-template';
import { PRINT_LETTERHEAD_FOOTER_KEY, PRINT_LETTERHEAD_HEADER_KEY } from '@/print-engine/company-letterhead';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Mapping between form fields and database setting keys (defined outside component to prevent recreation)
const FIELD_TO_SETTING_KEY_MAP: Record<string, string> = {
  company_name: 'company_name',
  trade_license: 'trade_license_number',
  tax_registration: 'tax_registration_number',
  vat_number: 'vat_registration_number',
  email: 'company_email',
  phone: 'company_phone',
  website: 'company_website',
  address: 'company_address',
  address_line1: 'company_address_line1',
  city: 'company_city',
  emirate_code: 'company_emirate_code',
  country_code: 'company_country_code',
  legal_registration_type: 'company_legal_registration_type',
  tax_scheme_code: 'company_tax_scheme_code',
  payment_means_code: 'company_payment_means_code',
  electronic_address: 'company_electronic_address',
  electronic_identifier: 'company_electronic_identifier',
  uae_business_process_type: 'uae_business_process_type',
  uae_specification_identifier: 'uae_specification_identifier',
  uae_transaction_type_code: 'uae_transaction_type_code',
  logo: 'company_logo',
  uae_fta_enabled: 'uae_fta_enabled',
  bank_name: 'bank_name',
  bank_account_no: 'bank_account_no',
  bank_iban: 'bank_iban',
  bank_routing_code: 'bank_routing_code',
  bank_swift_code: 'bank_swift_code',
  bank_address: 'bank_address',
  sales_estimation_cogs_account_id: 'sales_estimation_cogs_account_id',
  accrued_unbilled_cost_account_id: 'accrued_unbilled_cost_account_id',
  landed_cost_return_variance_account_id: 'landed_cost_return_variance_account_id',
  realized_fx_gain_loss_account_id: 'realized_fx_gain_loss_account_id',
};

const BOOLEAN_SETTING_KEYS = new Set(['uae_fta_enabled']);

/** Managed in Finance → Currencies (is_base_currency), not company_settings. */
const COMPANY_PROFILE_NON_SETTING_FIELDS = new Set(['base_currency']);

// Reverse mapping: database key to form field
const SETTING_KEY_TO_FIELD_MAP: Record<string, string> = Object.entries(FIELD_TO_SETTING_KEY_MAP).reduce(
  (acc, [formField, settingKey]) => {
    acc[settingKey] = formField;
    return acc;
  },
  {} as Record<string, string>
);

export const CompanyProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [logo, setLogo] = React.useState<UploadFile[]>([]);
  const [letterheadHeader, setLetterheadHeader] = useState('');
  const [letterheadFooter, setLetterheadFooter] = useState('');
  const [saving, setSaving] = useState(false);
  const companyId = useAuthStore((s) => s.user?.company_id);
  const { data: settings, isLoading, refetch } = useCompanyProfileSettings(companyId);
  const updateCompanySettings = useUpdateCompanyProfileSettings(companyId);
  const { data: currencies = [], refetch: refetchCurrencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => currencyService.getCurrencies(),
    enabled: !!companyId,
  });
  const { data: coaAccounts = [] } = useQuery({
    queryKey: ['chart-of-accounts', 'company-profile-gl'],
    queryFn: async () => {
      const res = await api.get('/finance/chart-of-accounts');
      const body = (res as { data?: unknown })?.data ?? res;
      const list = Array.isArray(body)
        ? body
        : Array.isArray((body as { data?: unknown })?.data)
          ? (body as { data: { id: string; account_code: string; account_name: string; is_group?: boolean; is_active?: boolean }[] }).data
          : [];
      return list as { id: string; account_code: string; account_name: string; is_group?: boolean; is_active?: boolean }[];
    },
    enabled: !!companyId,
  });
  const coaOptions = React.useMemo(
    () =>
      coaAccounts
        .filter((a) => a.is_active !== false && !a.is_group)
        .map((a) => ({
          value: a.id,
          label: `${a.account_code} — ${a.account_name}`,
        })),
    [coaAccounts]
  );
  const { refreshCurrencies } = useCurrency();

  // Map { [setting_key]: { value } } → form fields; set logo state separately
  const formValues = React.useMemo(() => {
    const values: Record<string, unknown> = {};
    let logoUrl: string | undefined;

    if (!settings || Object.keys(settings).length === 0) {
      return { values, logoUrl };
    }

    for (const [settingKey, entry] of Object.entries(settings)) {
      const raw = entry?.value;
      if (raw == null || String(raw).trim() === '') continue;
      const formField = SETTING_KEY_TO_FIELD_MAP[settingKey] || settingKey;

      if (settingKey === 'company_logo') {
        logoUrl = String(raw);
        continue;
      }
      if (settingKey === PRINT_LETTERHEAD_HEADER_KEY || settingKey === PRINT_LETTERHEAD_FOOTER_KEY) continue;
      if (BOOLEAN_SETTING_KEYS.has(settingKey)) {
        values[formField] = raw === 'true' || raw === '1';
      } else {
        values[formField] = raw;
      }
    }

    return { values, logoUrl };
  }, [settings]);

  const baseCurrencyMaster = React.useMemo(
    () => currencies.find((c) => c.is_base_currency),
    [currencies]
  );

  useEffect(() => {
    if (formValues.values && Object.keys(formValues.values).length > 0) {
      form.setFieldsValue(formValues.values);
    }
    if (formValues.logoUrl) {
      setLogo([
        {
          uid: '-1',
          name: 'company-logo',
          status: 'done',
          url: formValues.logoUrl,
        },
      ]);
    }
  }, [formValues, form]);

  useEffect(() => {
    setLetterheadHeader(String(settings?.[PRINT_LETTERHEAD_HEADER_KEY]?.value ?? ''));
    setLetterheadFooter(String(settings?.[PRINT_LETTERHEAD_FOOTER_KEY]?.value ?? ''));
  }, [settings]);

  const handleSave = async (values: Record<string, unknown>) => {
    if (!companyId) {
      message.error('No company is attached to your session. Cannot save company profile.');
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, { value: string; type: string; category: string }> = {};

      for (const [formField, value] of Object.entries(values)) {
        if (COMPANY_PROFILE_NON_SETTING_FIELDS.has(formField)) continue;
        const settingKey = FIELD_TO_SETTING_KEY_MAP[formField] || formField;
        const isBoolean = BOOLEAN_SETTING_KEYS.has(settingKey);
        if (!isBoolean && (value === undefined || value === null || value === '')) continue;
        if (isBoolean && value === undefined) continue;

        payload[settingKey] = {
          value: isBoolean ? (value ? 'true' : 'false') : String(value),
          type: isBoolean ? 'boolean' : 'string',
          category: 'company',
        };
      }

      if (logo.length > 0 && logo[0].url) {
        payload.company_logo = {
          value: String(logo[0].url),
          type: 'string',
          category: 'company',
        };
      }

      payload[PRINT_LETTERHEAD_HEADER_KEY] = { value: letterheadHeader, type: 'string', category: 'print' };
      payload[PRINT_LETTERHEAD_FOOTER_KEY] = { value: letterheadFooter, type: 'string', category: 'print' };

      const savedCount = Object.keys(payload).length;
      await updateCompanySettings.mutateAsync(payload);
      message.success(`Company profile updated successfully! (${savedCount} fields saved)`);
      refetch();
      refetchCurrencies();
      refreshCurrencies();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save company profile';
      message.error(`Failed to save company profile: ${errorMessage}`);
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const uploadProps = {
    maxCount: 1,
    fileList: logo,
    beforeUpload: (file: any) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return false;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        setLogo([
          {
            uid: file.uid,
            name: file.name,
            status: 'done',
            url: dataUrl,
            originFileObj: file,
          },
        ]);
      };
      reader.readAsDataURL(file);

      return false;
    },
    onChange: ({ fileList }: any) => {
      if (fileList.length === 0) {
        setLogo([]);
      }
    },
    onRemove: () => {
      setLogo([]);
      if (!companyId) return;
      updateCompanySettings
        .mutateAsync({
          company_logo: { value: '', type: 'string', category: 'company' },
        })
        .then(() => {
          message.success('Logo removed');
        })
        .catch((error) => {
          console.error('Failed to remove logo:', error);
        });
    },
  };

  if (!companyId) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="No company on your session"
          description="Your login is not attached to a company, so Company Profile cannot load. Sign in with a tenant user or ask an admin to assign a company."
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Spin size="large" tip="Loading company profile..." />
        </div>
      </Card>
    );
  }

  const loadedCount = settings ? Object.keys(settings).length : 0;

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <Space>
            <BuildOutlined />
            <span>Company Profile</span>
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary">Manage your company information</Text>
            {loadedCount > 0 && <Text type="success">({loadedCount} settings loaded)</Text>}
          </Space>
        }
      >
        <Alert
          message="Company Information"
          description="This information applies only to your company and will be used in reports, quotations, and other documents."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form form={form} layout="vertical" onFinish={handleSave} preserve={true}>
          
          {/* Basic Information */}
          <Title level={5}>Basic Information</Title>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Company Name" name="company_name" rules={[{ required: true }]}>
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Trade License Number" name="trade_license">
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Tax Registration Number" name="tax_registration">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="VAT Number" name="vat_number">
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Base currency"
                help="Set or change the base currency in Finance → Currencies (mark one currency as base)."
              >
                {baseCurrencyMaster ? (
                  <Space direction="vertical" size={4}>
                    <Text strong>
                      {baseCurrencyMaster.currency_code} — {baseCurrencyMaster.currency_name}
                    </Text>
                    <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate('/fin/currencies')}>
                      Open currency master
                    </Button>
                  </Space>
                ) : (
                  <Space direction="vertical" size={4}>
                    <Text type="secondary">No base currency defined yet.</Text>
                    <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate('/fin/currencies')}>
                      Add currencies
                    </Button>
                  </Space>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Contact Information */}
          <Title level={5}>Contact Information</Title>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Phone" name="phone">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Website" name="website">
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Address" name="address">
            <TextArea rows={3} />
          </Form.Item>

          <Divider />

          <Title level={5}>Bank Details (for invoice print)</Title>
          <Alert
            message="Shown on printed tax invoices"
            description="These details appear in the bottom-left footer of the tax invoice print layout (bank name, account, IBAN, routing, SWIFT, branch address)."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Bank name" name="bank_name">
                <Input size="large" placeholder="e.g. First Abu Dhabi Bank" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Bank account no." name="bank_account_no">
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="IBAN" name="bank_iban">
                <Input size="large" placeholder="AE…" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Routing code" name="bank_routing_code">
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="SWIFT / BIC" name="bank_swift_code">
                <Input size="large" placeholder="e.g. NBADAEAA" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Bank branch address" name="bank_address">
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Address line 1 (structured)" name="address_line1">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="City" name="city">
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Emirate" name="emirate_code">
                <Select size="large" showSearch optionFilterProp="label" options={UAE_EMIRATES} allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Country code" name="country_code" initialValue="AE">
                <Input size="large" maxLength={2} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Payment means code" name="payment_means_code">
                <Select size="large" options={UAE_PAYMENT_MEANS} allowClear />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Legal registration type" name="legal_registration_type">
                <Select size="large" options={LEGAL_REGISTRATION_TYPES} allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Tax scheme code" name="tax_scheme_code" initialValue="VAT">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Electronic address" name="electronic_address">
                <Input size="large" placeholder="email or Peppol endpoint" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Electronic identifier" name="electronic_identifier">
            <Input size="large" />
          </Form.Item>

          <Divider />

          <Title level={5}>UAE E-Invoicing</Title>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Business process type" name="uae_business_process_type">
                <Input size="large" placeholder="urn:peppol:bis:billing" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Specification identifier" name="uae_specification_identifier">
                <Input size="large" placeholder="urn:peppol:pint:billing-1@ae-1" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Transaction type code" name="uae_transaction_type_code">
            <Input size="large" placeholder="1000000" />
          </Form.Item>
          <Alert
            message="FTA / ASP live submission"
            description="Configure Aigentrix (TronStride FZC) under Compliance → UAE E-Invoicing (ASP connection panel). All ASP credentials and endpoints are set there — not via server environment. Seller identity on Peppol documents always comes from this company profile (ORDAN does not appear as the taxpayer)."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form.Item
            label="Enable legacy local FTA stub (non-ASP)"
            name="uae_fta_enabled"
            valuePropName="checked"
            help="Only affects the local uae-fta adapter when no ASP connection is enabled. Prefer the ASP connection panel for live Aigentrix submission."
          >
            <Switch />
          </Form.Item>

          <Divider />

          <Title level={5}>Sales & inventory GL defaults</Title>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Used for Delivery Note COGS, Material Issue, and non-stock Goods Receipts / Service Receipts"
            description={
              <span>
                Sales Estimation COGS defaults to 5002 (DN only). Accrued Cost defaults to 2170 (MI debit and
                non-stock GRN / service receipt debit). Manage leaf accounts in{' '}
                <Link to="/fin/gl/chart-of-accounts">Finance → Chart of accounts</Link>.
              </span>
            }
          />
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Sales Estimation COGS account"
                name="sales_estimation_cogs_account_id"
                help="Debit side for DN COGS only (stock and Sales Estimation non-stock). Not used on GRN."
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select COA (default 5002)"
                  options={coaOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Accrued Cost / Unbilled Cost Liability"
                name="accrued_unbilled_cost_account_id"
                help="Credit on Sales Estimation DN COGS; debit on Material Issue and non-stock GRN / service receipt."
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select COA (default 2170)"
                  options={coaOptions}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Landed Cost Return Variance account"
                name="landed_cost_return_variance_account_id"
                help="Debit for the landed-cost (freight / overhead) share when goods are returned to the supplier. No default: required before posting a return of goods that carried landed cost."
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select COA (no default)"
                  options={coaOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Exchange Gain/Loss account"
                name="realized_fx_gain_loss_account_id"
                help="Realised exchange difference when a foreign-currency invoice is received or paid at a rate different from the invoice rate. No default: required before such a receipt / payment can post."
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select COA (no default)"
                  options={coaOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Company Logo */}
          <Title level={5}>Company Logo</Title>
          <Form.Item label="Upload Logo" help="Recommended size: 200x200px, PNG or JPG">
            <Upload {...uploadProps} listType="picture-card">
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            </Upload>
          </Form.Item>

          <Divider />

          <Title level={5}>Print Letterhead</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Header and footer artwork used on every printed and PDF document (quotations, invoices, delivery notes,
            purchase orders, reports and statements). Documents printed on preprinted stationery leave these bands blank.
            Changes apply after you save.
          </Text>
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Form.Item label="Header">
                <LetterheadImageField
                  value={letterheadHeader}
                  onChange={setLetterheadHeader}
                  fallbackSrc={DEFAULT_LETTERHEAD.headerSrc}
                  hint="Wide banner, PNG or JPG up to 2 MB (about 2550 × 300 px)."
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Footer">
                <LetterheadImageField
                  value={letterheadFooter}
                  onChange={setLetterheadFooter}
                  fallbackSrc={DEFAULT_LETTERHEAD.footerSrc}
                  hint="Full page width, PNG or JPG up to 2 MB (about 2550 × 300 px)."
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Button 
            type="primary" 
            htmlType="submit" 
            icon={<SaveOutlined />} 
            size="large"
            loading={saving}
          >
            {saving ? 'Saving...' : 'Save Company Profile'}
          </Button>
        </Form>
      </Card>
    </div>
  );
};

